import { memo, useState } from 'react';

import { useLocation } from 'react-router-dom';

import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';

import themeConfig from '@config/theme.config';
import { HEADER_HEIGHT } from '@constants/layout';
import { useAuth } from '@hooks';
import { getPageTitle } from '@utils/navigation';

import { MENU_ITEM_ICON_SIZE, MENU_ITEM_ICON_SX, MENU_ITEM_SX } from '../menuItemStyles';

const getDisplayName = (user) => {
  if (!user) return 'User';
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return fullName || user.email || 'User';
};

const getInitials = (user) => {
  const first = user?.firstName?.[0] || '';
  const last = user?.lastName?.[0] || '';
  return `${first}${last}`.toUpperCase() || 'U';
};

/**
 * Slim premium top bar — page context + account menu.
 * Does not change routing; title is derived from current path.
 */
const AppHeader = memo(function AppHeader({ showMenuButton = false, onMenuClick }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const displayName = getDisplayName(user);
  const pageTitle = getPageTitle(location.pathname);

  return (
    <AppBar
      position="sticky"
      color="inherit"
      elevation={0}
      sx={{
        height: HEADER_HEIGHT,
        borderBottom: '1px solid',
        borderColor: themeConfig.borderColor,
        bgcolor: 'rgba(255,255,255,0.86)',
        backdropFilter: 'blur(10px)',
      }}
    >
      <Toolbar
        sx={{
          minHeight: `${HEADER_HEIGHT}px !important`,
          px: { xs: 2, sm: 3, lg: 4 },
          gap: 1.5,
        }}
      >
        {showMenuButton ? (
          <IconButton
            edge="start"
            aria-label="Open navigation menu"
            onClick={onMenuClick}
            sx={{
              color: themeConfig.textPrimary,
              bgcolor: themeConfig.surfaceSecondary,
              borderRadius: '10px',
              '&:hover': { bgcolor: themeConfig.borderColor },
            }}
          >
            <MenuIcon />
          </IconButton>
        ) : null}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            noWrap
            sx={{
              fontSize: 15,
              fontWeight: 600,
              letterSpacing: '-0.02em',
              color: themeConfig.textPrimary,
            }}
          >
            {pageTitle}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right', minWidth: 0 }}>
            <Typography
              noWrap
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: themeConfig.textPrimary,
                lineHeight: 1.25,
              }}
            >
              {displayName}
            </Typography>
            {user?.email ? (
              <Typography noWrap sx={{ fontSize: 12, color: themeConfig.textSecondary }}>
                {user.email}
              </Typography>
            ) : null}
          </Box>

          <IconButton
            onClick={(event) => setAnchorEl(event.currentTarget)}
            aria-label="Open account menu"
            aria-controls={menuOpen ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={menuOpen ? 'true' : undefined}
            sx={{ p: 0.35 }}
          >
            <Avatar
              src={user?.profile_avatar || undefined}
              alt={displayName}
              sx={{
                width: 36,
                height: 36,
                bgcolor: themeConfig.primaryColor,
                fontSize: 13,
                fontWeight: 700,
                border: '1px solid',
                borderColor: themeConfig.borderColor,
              }}
            >
              {getInitials(user)}
            </Avatar>
          </IconButton>

          <Menu
            id="account-menu"
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: {
                sx: {
                  mt: 1,
                  minWidth: 220,
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: themeConfig.borderColor,
                  boxShadow: themeConfig.shadowMd,
                  overflow: 'hidden',
                },
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: themeConfig.textPrimary,
                  lineHeight: 1.3,
                }}
                noWrap
              >
                {displayName}
              </Typography>
              {user?.email ? (
                <Typography
                  noWrap
                  sx={{ fontSize: 12, color: themeConfig.textSecondary, mt: 0.25 }}
                >
                  {user.email}
                </Typography>
              ) : null}
            </Box>
            <Divider sx={{ borderColor: themeConfig.borderColor }} />
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                logout();
              }}
              sx={{
                ...MENU_ITEM_SX,
                mx: 0.75,
                my: 0.75,
                '&:hover': {
                  bgcolor: themeConfig.roseSoft,
                  color: themeConfig.roseIcon,
                },
                '&.Mui-focusVisible': {
                  bgcolor: themeConfig.roseSoft,
                  color: themeConfig.roseIcon,
                },
              }}
            >
              <ListItemIcon sx={MENU_ITEM_ICON_SX}>
                <LogoutOutlinedIcon sx={{ fontSize: MENU_ITEM_ICON_SIZE }} />
              </ListItemIcon>
              Sign out
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
});

AppHeader.propTypes = {
  showMenuButton: PropTypes.bool,
  onMenuClick: PropTypes.func,
};

export default AppHeader;
